import React from 'react'
import Select from 'react-select'
import type { CSSObjectWithLabel, StylesConfig } from 'react-select'
import { TxPlanStatus, TX_PLAN_STATUS } from '@dataTypes/treatmentPlan.types'

interface StatusOption {
  value: TxPlanStatus
  label: string
  isDisabled?: boolean
}

interface TxPlanStatusDropdownProps {
  value: TxPlanStatus
  onChange?: (newStatus: TxPlanStatus) => void
  readOnly?: boolean
}

const baseOptions: StatusOption[] = [
  { value: TX_PLAN_STATUS.PROPOSED, label: TX_PLAN_STATUS.PROPOSED },
  { value: TX_PLAN_STATUS.ACCEPTED, label: TX_PLAN_STATUS.ACCEPTED },
  { value: TX_PLAN_STATUS.COMPLETED, label: TX_PLAN_STATUS.COMPLETED },
  { value: TX_PLAN_STATUS.REJECTED, label: TX_PLAN_STATUS.REJECTED },
]

const statusStyles: Record<
  TxPlanStatus,
  { backgroundColor: string; color: string }
> = {
  [TX_PLAN_STATUS.ACCEPTED]: {
    backgroundColor: 'var(--vivid-green-200)',
    color: 'var(--vivid-green-700)',
  },
  [TX_PLAN_STATUS.PROPOSED]: {
    backgroundColor: '#C6F0ED',
    color: '#133539',
  },
  [TX_PLAN_STATUS.REJECTED]: {
    backgroundColor: '#FFF2F1',
    color: '#9E1F16',
  },
  [TX_PLAN_STATUS.COMPLETED]: {
    backgroundColor: '#D3E6FF',
    color: '#133539',
  },
}

const getStatusStyle = (
  status: TxPlanStatus | undefined,
): { backgroundColor: string; color: string } =>
  status ? statusStyles[status] : statusStyles[TX_PLAN_STATUS.PROPOSED]

const optionStyle = {
  backgroundColor: 'var(--gray-white)',
  color: 'var(--gray-700)',
}

const getCustomStyles = (
  readOnly: boolean,
): StylesConfig<StatusOption, false> => ({
  control: (base, state) => {
    const selected = state.getValue()[0]?.value
    const style = getStatusStyle(selected)
    return {
      ...base,
      borderRadius: 8,
      border: 'none',
      boxShadow: 'none',
      minHeight: '24px',
      height: '24px',
      paddingRight: 0,
      backgroundColor: style.backgroundColor,
      color: style.color,
      fontSize: '14px',
      fontWeight: 500,
      cursor: readOnly ? 'default' : 'pointer',
      opacity: 1,
    } as CSSObjectWithLabel
  },
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    height: '24px',
  }),
  singleValue: (base, { data }) => ({
    ...base,
    color: getStatusStyle(data.value).color,
    fontSize: '14px',
    margin: 0,
  }),
  option: (base, { data, isFocused, isDisabled }) => ({
    ...base,
    backgroundColor: isDisabled
      ? '#f2f2f2'
      : isFocused
        ? 'var(--gray-100)'
        : optionStyle.backgroundColor,
    color: isDisabled
      ? data.value === TX_PLAN_STATUS.REJECTED
        ? 'var(--red-400)'
        : '#999'
      : data.value === TX_PLAN_STATUS.REJECTED
        ? 'var(--red-500)'
        : optionStyle.color,
    cursor: isDisabled ? 'default' : 'pointer',
    fontSize: '14px',
    padding: '6px 12px',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: () => ({ display: 'none' }),
  indicatorsContainer: () => ({ display: 'none' }),
  menu: (base) => ({
    ...base,
    zIndex: 1060,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1060,
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
  }),
})

const TxPlanStatusDropdown: React.FC<TxPlanStatusDropdownProps> = ({
  value,
  onChange,
  readOnly = false,
}): JSX.Element => {
  const options = baseOptions.map((opt) => ({
    ...opt,
    isDisabled: opt.value === value,
  }))

  const handleChange = (newValue: StatusOption | null): void => {
    if (
      newValue &&
      Object.values(TX_PLAN_STATUS).includes(newValue.value) &&
      newValue.value !== value
    ) {
      onChange?.(newValue.value)
    }
  }

  return (
    <Select<StatusOption, false>
      className={`tx-plan-status-dropdown${readOnly ? ' tx-plan-status-dropdown--readonly' : ''}`}
      classNamePrefix="tx-plan-status"
      value={options.find((opt) => opt.value === value)}
      onChange={readOnly ? undefined : handleChange}
      options={options}
      styles={getCustomStyles(readOnly)}
      isSearchable={false}
      isDisabled={readOnly}
      openMenuOnClick={!readOnly}
      openMenuOnFocus={!readOnly}
      tabIndex={readOnly ? -1 : undefined}
      menuPlacement="bottom"
      menuPosition="fixed"
      menuPortalTarget={document.body}
      menuShouldScrollIntoView={false}
    />
  )
}

export default TxPlanStatusDropdown
